import { FieldType, ITemplateField, ITemplatePreview } from "constants/template-types";
import { ATS_CUSTOM_TEMPLATES } from "constants/action-types";
import { DEFAULT_TEMPLATE_NAME } from "constants/ats-exports";
import type { IRequestAction, IRequestDataAction } from "epics/base-epic";
import { fromJS } from "immutable";
import { Dispatch } from "react-redux";
import type { IStore } from "reducers";

export interface ICreateAtsCustomTemplateActionPayload extends IRequestDataAction {}
export interface IUpdateAtsCustomTemplateActionPayload extends IRequestDataAction {}
export interface IAddRowActionPayload extends IRequestDataAction<{ type: FieldType; header: string; field: string }> {}
export interface IDeleteRowActionPayload extends IRequestAction {
    fieldId: string;
}

export interface IUpdateRowActionPayload extends IRequestDataAction {
    fieldId: string;
}

export interface IUpdateRowSortActionPayload extends IRequestDataAction {
    fieldIndex: number;
    dropIndex: number;
}

const selectTemplate = (state: IStore) => state.atsCustomTemplates.template.payload;
const selectTemplateList = (state: IStore) => state.atsCustomTemplates.list.payload;

export const createTemplate = () => ({
    type: ATS_CUSTOM_TEMPLATES.CREATE.START,
    payload: {
        url: "/api/v3.0/inventory/ats-report/export-formats",
        body: {
            name: DEFAULT_TEMPLATE_NAME,
        },
    },
});

export const fetchTemplate = (templateId: string) => ({
    type: ATS_CUSTOM_TEMPLATES.FETCH.START,
    payload: { url: `/api/v3.0/inventory/ats-report/export-formats/${templateId}` },
});

export const fetchTempates = () => ({
    type: ATS_CUSTOM_TEMPLATES.LIST.FETCH.START,
    payload: { url: "/api/v3.0/inventory/ats-report/export-formats" },
});

export const updateTemplate =
    (templateId: string, changes: Partial<ITemplatePreview>) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
        const template = selectTemplate(getState());

        dispatch({
            type: ATS_CUSTOM_TEMPLATES.UPDATE,
            payload: {
                ...template,
                ...changes,
            },
        });
        dispatch({
            type: ATS_CUSTOM_TEMPLATES.MUTATE.START,
            payload: { url: `/api/v3.0/inventory/ats-report/export-formats/${templateId}`, body: changes },
        });
    };

export const updateTemplateFromList =
    (templateId: string, changes: Partial<ITemplatePreview>) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
        const list = selectTemplateList(getState());

        const resetDefault = (template) => ({
            ...template,
            default_format: false,
        });

        const templates = changes.default_format ? list.map(resetDefault) : list;

        const updatedTemplates = templates.map((template) =>
            template.id === templateId
                ? {
                      ...template,
                      ...changes,
                  }
                : template
        );

        dispatch({
            type: ATS_CUSTOM_TEMPLATES.LIST.UPDATE,
            payload: updatedTemplates,
        });
        dispatch({
            type: ATS_CUSTOM_TEMPLATES.MUTATE.START,
            payload: { url: `/api/v3.0/inventory/ats-report/export-formats/${templateId}`, body: changes },
        });
    };

export const deleteTemplate = (templateId: string) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
    dispatch({
        type: ATS_CUSTOM_TEMPLATES.DELETE.START,
        payload: { url: `/api/v3.0/inventory/ats-report/export-formats/${templateId}` },
    });
};

export const deleteTemplateFromList = (templateId: string) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
    const list = selectTemplateList(getState());

    dispatch({
        type: ATS_CUSTOM_TEMPLATES.LIST.UPDATE,
        payload: list.filter((template) => template.id !== templateId),
    });
    dispatch({
        type: ATS_CUSTOM_TEMPLATES.DELETE.START,
        payload: {
            url: `/api/v3.0/inventory/ats-report/export-formats/${templateId}`,
        },
    });
};

export const addTemplateRow =
    (options: { type: FieldType; header: string; field: string }) =>
    (dispatch: Dispatch<{}>, getState: () => IStore) => {
        const template = selectTemplate(getState());

        dispatch({
            type: ATS_CUSTOM_TEMPLATES.FIELD.ADD.START,
            payload: {
                url: `/api/v3.0/inventory/ats-report/export-formats/${template.id}/field`,
                body: options,
            },
        });
    };

export const deleteTemplateRow = (fieldId: string) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
    const template = selectTemplate(getState());

    dispatch({
        type: ATS_CUSTOM_TEMPLATES.FIELD.DELETE.START,
        payload: {
            fieldId,
            url: `/api/v3.0/inventory/ats-report/export-formats/${template.id}/field/${fieldId}`,
        },
    });
};

export const updateTemplateRow =
    (fieldId: string, changes: Partial<ITemplateField>) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
        const template = selectTemplate(getState());

        const fieldIndex = template.fields.findIndex(({ id }) => id === fieldId);
        const { field } = template.fields[fieldIndex];
        const templateWithUpdatedRow = fromJS(template)
            .updateIn(["fields"], (fields) => fields.update(fieldIndex, (field) => field.merge(changes)))
            .toJS();

        dispatch({
            type: ATS_CUSTOM_TEMPLATES.UPDATE,
            payload: templateWithUpdatedRow,
        });

        dispatch({
            type: ATS_CUSTOM_TEMPLATES.FIELD.MUTATE.START,
            payload: {
                fieldId,
                url: `/api/v3.0/inventory/ats-report/export-formats/${template.id}/field/${fieldId}`,
                body: { field, ...changes },
            },
        });
    };

export const updateTemplateRowSort =
    (fieldId: string, afterId: string) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
        const template = selectTemplate(getState());

        const dropIndex = afterId ? template.fields.findIndex(({ id }) => id === afterId) : 0;
        const fieldIndex = template.fields.findIndex(({ id }) => id === fieldId);

        const templateWithUpdatedRowSort = fromJS(template)
            .updateIn(["fields"], (fields) => fields.splice(fieldIndex, 1).splice(dropIndex, 0, fields.get(fieldIndex)))
            .toJS();

        const beforeId = templateWithUpdatedRowSort.fields[dropIndex - 1]?.id;

        dispatch({
            type: ATS_CUSTOM_TEMPLATES.UPDATE,
            payload: templateWithUpdatedRowSort,
        });

        dispatch({
            type: ATS_CUSTOM_TEMPLATES.FIELD.SORT.START,
            payload: {
                dropIndex,
                fieldIndex,
                url: `/api/v3.0/inventory/ats-report/export-formats/${template.id}/fields/sort`,
                body: {
                    field: fieldId,
                    before: beforeId,
                },
            },
        });
    };
